import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone, ShieldCheck } from "lucide-react";
import { CONTACT, FAQS, IMAGES, IMMIGRATION_SERVICES, PROCESS_STEPS, TESTIMONIALS, VALUES } from "@/data/site";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CTABand } from "@/components/CTABand";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { MaskedLines, Reveal, ScaleIn } from "@/components/Motion";
import { ServiceCard } from "@/components/ServiceCard";
import { StructuredData } from "@/components/StructuredData";

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://egaalservices.com/#immigration-faqs",
  "url": "https://egaalservices.com/#home-faq-section",
  "name": "Egaal Tax & Immigration Services FAQs",
  "inLanguage": "en-US",
  "isPartOf": {
    "@type": "WebSite",
    "@id": "https://egaalservices.com/#website",
    "name": "Egaal Tax & Immigration Services",
    "url": "https://egaalservices.com"
  },
  "mainEntity": FAQS.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-brand-navy text-white" data-testid="home-hero-section">
      <div className="absolute -left-40 top-20 h-96 w-96 rounded-full bg-brand-copper/20 blur-3xl" />
      <div className="section-shell grid min-h-[calc(100vh-5rem)] items-center gap-14 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
        <div className="relative z-10">
          <Reveal>
            <div className="mb-7">
              <p className="flex items-center gap-3 text-brand-copper">
                <span className="h-px w-10 bg-brand-copper" />
                <span className="font-serif text-2xl font-bold tracking-tight sm:text-3xl">Egaal Tax & Immigration Services</span>
              </p>
              <p className="overline mt-4 text-white/58">Immigration-focused multi-service office</p>
            </div>
          </Reveal>
          <h1 className="font-serif text-4xl font-black leading-[0.94] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLines lines={["Helping Families", "Navigate U.S. Immigration", "With Confidence"]} />
          </h1>
          <Reveal delay={0.48}>
            <p className="mt-8 max-w-2xl text-base leading-8 text-white/74 sm:text-lg">
              Professional immigration document preparation, family petition support, USCIS application organization, NVC guidance, consular processing assistance, and certified translation coordination — all in one trusted office.
            </p>
          </Reveal>
          <Reveal delay={0.62} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Button asChild size="lg" className="rounded-full bg-brand-copper px-8 text-brand-navy hover:bg-brand-copper/90">
              <Link to="/contact" data-testid="home-hero-schedule-cta">
                Schedule an Immigration Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-transparent px-8 text-white hover:bg-white hover:text-brand-navy">
              <Link to="/immigration-services" data-testid="home-hero-services-cta">Explore Immigration Services</Link>
            </Button>
          </Reveal>
          <Reveal delay={0.75} className="mt-12 grid max-w-xl grid-cols-3 divide-x divide-white/12 border-y border-white/12 py-5">
            {["USCIS", "NVC", "Consular"].map((label) => (
              <div key={label} className="px-4 first:pl-0">
                <p className="font-serif text-xl font-bold text-brand-copper">{label}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-white/48">Support</p>
              </div>
            ))}
          </Reveal>
        </div>
        <div className="relative hidden min-h-[620px] lg:block" data-testid="home-hero-visual">
          <motion.div style={{ y: imageY }} className="image-spotlight absolute right-0 top-0 w-[78%]">
            <img src={IMAGES.family} alt="Family together after immigration support" className="hero-frame aspect-[4/5] w-full object-cover shadow-editorial" />
          </motion.div>
          <motion.div style={{ y: cardY }} className="absolute bottom-10 left-0 w-[54%] animate-float">
            <img src={IMAGES.passport} alt="Passport and immigration travel documents" className="aspect-[4/3] w-full rounded-lg border-8 border-brand-cream object-cover shadow-editorial" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="absolute right-4 top-12 max-w-56 rounded-lg bg-white/10 p-5 shadow-glow backdrop-blur-xl"
          >
            <ShieldCheck className="mb-4 h-8 w-8 text-brand-copper" />
            <p className="font-serif text-xl font-bold">Confidential document preparation</p>
            <p className="mt-2 text-xs leading-5 text-white/62">Organized support at every step.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <StructuredData id="home-faq-schema" data={FAQ_SCHEMA} />
      <HomeHero />
      <EditorialMarquee />
      <section className="section-padding" data-testid="home-immigration-services-section">
        <div className="section-shell">
          <Reveal className="mb-14 grid gap-8 lg:grid-cols-[0.75fr_1fr]">
            <div>
              <p className="overline text-brand-copper">Primary Focus</p>
              <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                Immigration services placed first — because your family’s future matters.
              </h2>
            </div>
            <p className="max-w-2xl self-end text-base leading-8 text-brand-navy/68">
              From family petitions to citizenship, Egaal organizes complicated requirements into a clear path forward. We prepare documents carefully, explain each step plainly, and support clients throughout the United States.
            </p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {IMMIGRATION_SERVICES.slice(0, 6).map((service, index) => (
              <ServiceCard key={service.slug} service={service} index={index} compact />
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding bg-white" data-testid="home-why-choose-section">
        <div className="section-shell grid items-center gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          <ScaleIn className="relative order-2 lg:order-1">
            <img src={IMAGES.consultation} alt="Professional consultation at Egaal office" className="aspect-[4/5] rounded-lg object-cover shadow-editorial" />
            <div className="absolute -bottom-8 -right-4 max-w-xs rounded-lg bg-brand-navy p-6 text-white shadow-editorial sm:-right-8">
              <p className="font-serif text-4xl font-black text-brand-copper">1:1</p>
              <p className="mt-2 text-sm font-bold">Personalized attention for every family</p>
            </div>
          </ScaleIn>
          <div className="order-1 lg:order-2">
            <Reveal>
              <p className="overline text-brand-copper">Why Choose Egaal</p>
              <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                Organized. Confidential. Culturally aware. Personally guided.
              </h2>
            </Reveal>
            <div className="mt-12 space-y-8">
              {VALUES.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Reveal key={value.title} delay={index * 0.08} className="flex gap-6 border-b border-brand-navy/10 pb-8 last:border-b-0">
                    <span className="font-serif text-4xl font-black text-brand-copper/50">{String(index + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-brand-copper" />
                        <h3 className="font-serif text-2xl font-bold">{value.title}</h3>
                      </div>
                      <p className="mt-3 text-sm leading-7 text-brand-navy/66">{value.description}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section className="section-padding" data-testid="home-process-section">
        <div className="section-shell">
          <Reveal className="mb-14 max-w-3xl">
            <p className="overline text-brand-copper">Our Process</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">From first question to prepared packet.</h2>
          </Reveal>
          <div className="grid gap-px overflow-hidden rounded-lg bg-brand-navy/10 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS_STEPS.map((step, index) => (
              <Reveal key={step.number} delay={index * 0.08} className="bg-brand-sand p-8">
                <p className="font-serif text-5xl font-black text-brand-copper/45">{step.number}</p>
                <h3 className="mt-8 font-serif text-2xl font-bold">{step.title}</h3>
                <p className="mt-4 text-sm leading-7 text-brand-navy/65">{step.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding bg-brand-cream" data-testid="home-testimonials-section">
        <div className="section-shell grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
          <Reveal>
            <p className="overline text-brand-copper">Client Experience</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">Families value clarity when the stakes are personal.</h2>
            <p className="mt-5 text-sm leading-7 text-brand-navy/64">Representative client feedback themes; identifying details are withheld for confidentiality.</p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((item, index) => (
              <Reveal key={item.context} delay={index * 0.1} className="rounded-lg bg-white p-7 shadow-sm">
                <CheckCircle2 className="mb-8 h-6 w-6 text-brand-copper" />
                <blockquote className="font-serif text-xl font-bold leading-8">“{item.quote}”</blockquote>
                <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-brand-navy/50">{item.context}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding" data-testid="home-faq-section">
        <div className="section-shell grid gap-12 lg:grid-cols-[0.75fr_1.25fr]">
          <Reveal>
            <p className="overline text-brand-copper">Questions</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">Clear answers before you begin.</h2>
            <Button asChild className="mt-8 rounded-full bg-brand-navy text-white hover:bg-brand-ink">
              <Link to="/contact" data-testid="home-faq-question-cta">Ask About Your Situation</Link>
            </Button>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="rounded-lg bg-white px-6 shadow-sm">
              {FAQS.map((faq, index) => (
                <AccordionItem key={faq.question} value={`item-${index}`}>
                  <AccordionTrigger data-testid={`home-faq-trigger-${index}`} className="font-serif text-lg font-bold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-7 text-brand-navy/68">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
      <CTABand id="home" title="From USCIS to NVC and U.S. Embassies — support at every step." />
    </>
  );
}
